import { formatClusterFilter, getFormatHealthStatFilter } from './helpers';
import groupBy from 'lodash/groupBy';
import { tooltips } from '@/copy';

export const createClusterFilters = (clusters) =>
    tooltips.clusters
        .filter(({ value }) => clusters.includes(value))
        .map(formatClusterFilter);

export const createHealthStatFilters = (healthStats) =>
    healthStats
        .map(([metricLabel, scores]) => {
            const { scoreLabels, scoreDefinitions } =
                tooltips.healthStats[metricLabel];
            const formatter = getFormatHealthStatFilter(metricLabel, {
                scoreLabels,
                scoreDefinitions,
            });
            const sortedScores = Object.keys(scoreLabels)
                .map((score) => parseInt(score, 10))
                .sort((a, b) => b - a);

            return scores.map((score) => {
                const idx = sortedScores.indexOf(score);
                return formatter(score, idx > 0 ? sortedScores[idx - 1] : 11);
            });
        })
        .flat();

// Groups outside this set — name/search, life_cycle, flavors — are orthogonal
// to presets: they neither de-match a chip nor get cleared by one.
export const getPresetManagedKeys = (presets) =>
    new Set(
        Object.values(presets || {}).flatMap(({ filters: presetFilters }) =>
            Object.keys(presetFilters || {}),
        ),
    );

export const findAppliedFilters = ({ filters, presets, sortOption }) => {
    if (!filters || !Object.keys(filters).length) return null;

    const presetManagedKeys = getPresetManagedKeys(presets);

    return Object.values(presets).filter(
        ({ filters: presetFilters, sortOption: presetSortOption }) => {
            if (sortOption && presetSortOption) {
                if (
                    sortOption.value !== presetSortOption.value ||
                    sortOption.dataSource !== presetSortOption.dataSource
                ) {
                    return false;
                }
            }

            const activeFilterGroups = Object.keys(filters).filter(
                (key) => presetManagedKeys.has(key) && filters[key]?.length > 0,
            );
            const presetFilterGroups = Object.keys(presetFilters).filter(
                (key) => presetFilters[key]?.length > 0,
            );
            // The New chips carry no filters until week/year resolve; without
            // this an empty preset matches whatever is applied.
            if (!presetFilterGroups.length) return false;
            if (activeFilterGroups.length !== presetFilterGroups.length)
                return false;
            if (
                !presetFilterGroups.every((key) =>
                    activeFilterGroups.includes(key),
                )
            )
                return false;

            return Object.entries(presetFilters).every(
                ([filterGroup, presetItems]) => {
                    const currentItems = filters[filterGroup] || [];

                    const groupBySubGroup = (items) =>
                        items.reduce((acc, item) => {
                            const group = item.filterSubGroup || filterGroup;
                            acc[group] = acc[group] || [];
                            acc[group].push(item.value);
                            return acc;
                        }, {});

                    const presetGroups = groupBySubGroup(presetItems);
                    const currentGroups = groupBySubGroup(currentItems);

                    return Object.entries(presetGroups).every(
                        ([subGroup, presetValues]) => {
                            const currentValues = currentGroups[subGroup];
                            if (!currentValues) return false;
                            const presetSet = new Set(presetValues);
                            const currentSet = new Set(currentValues);

                            if (presetSet.size !== currentSet.size)
                                return false;
                            for (const val of presetSet) {
                                if (!currentSet.has(val)) return false;
                            }
                            return true;
                        },
                    );
                },
            );
        },
    );
};

export const applyPresetToFilters = (
    currentFilters,
    preset,
    mode = 'select',
    presetManagedKeys,
) => {
    if (mode === 'select') {
        // Keep the groups presets don't own, or this wipes the column search.
        const updatedFilters = {};
        if (presetManagedKeys) {
            for (const key of Object.keys(currentFilters || {})) {
                if (!presetManagedKeys.has(key))
                    updatedFilters[key] = currentFilters[key];
            }
        }
        for (const key in preset) {
            if (!preset.hasOwnProperty(key)) continue;
            updatedFilters[key] = [...preset[key]];
        }
        return { updatedFilters };
    }

    const updatedFilters = { ...currentFilters };
    for (const key in preset) {
        if (!preset.hasOwnProperty(key)) continue;

        const presetItems = preset[key];
        const currentItems = currentFilters[key] || [];

        const hasSubGroups = presetItems.some((item) =>
            item.hasOwnProperty('filterSubGroup'),
        );

        if (hasSubGroups) {
            const presetByGroup = groupBy(presetItems, 'filterSubGroup');
            const currentByGroup = groupBy(currentItems, 'filterSubGroup');

            updatedFilters[key] = Object.keys(currentByGroup)
                .filter((group) => !presetByGroup[group])
                .map((group) => currentByGroup[group])
                .flat();
        } else {
            updatedFilters[key] = [];
        }
    }

    return { updatedFilters };
};

export const createPresetOnChangeHandler = ({
    filters,
    setFilters,
    sortOption,
    setSortOption,
    initialSortOption,
    theme,
    presets,
}) => {
    const presetManagedKeys = presets
        ? getPresetManagedKeys(presets)
        : undefined;
    return ({ action, option }) => {
        const mode = action === 'select-option' ? 'select' : 'remove';
        const { updatedFilters } = applyPresetToFilters(
            filters,
            option.filters,
            mode,
            presetManagedKeys,
        );
        switch (action) {
            case 'select-option':
                setFilters(updatedFilters);
                if (
                    option.sortOption.value !== sortOption.value ||
                    option.sortOption.dataSource !== sortOption.dataSource
                ) {
                    setSortOption(option.sortOption);
                }
                break;

            case 'remove-value':
                setFilters(updatedFilters);
                if (
                    initialSortOption.value !== sortOption.value ||
                    option.sortOption.dataSource !== sortOption.dataSource
                ) {
                    setSortOption(initialSortOption);
                }
                break;

            default:
                throw new Error(`Unexpected action type "${action}"`);
        }
    };
};
